// next 의 Metadata 대체 — 제목 규칙(브랜드 화면은 「· 빌드파이」, 고객 화면은 시공사 제목 그대로)을 route head 로 옮긴다.
import { CUSTOMER_METADATA } from "@/pages/customer-metadata";
import { BRAND_NAME } from "@buildpy/ui/brand-paths";

// next 처럼 template 은 선택값 — 없으면 하위 제목을 그대로 쓴다.
export interface TitleTemplate {
  template?: string;
  absolute: string;
}

// next 의 robots 객체 — head 에는 「noindex, nofollow」 한 줄로 나간다.
export interface Robots {
  index: boolean;
  follow: boolean;
}

export interface Metadata {
  title?: string | TitleTemplate;
  robots?: Robots | null;
  description?: string | null;
  openGraph?: unknown;
  twitter?: unknown;
  icons?: unknown[] | null;
  manifest?: string | null;
  applicationName?: string | null;
}

export const ROOT_DESCRIPTION = "인테리어 시공사의 상담 · 견적 · 계약 콘솔";

export const ROOT_TITLE: TitleTemplate = { template: `%s · ${BRAND_NAME}`, absolute: BRAND_NAME };

const frameOf = (title: Metadata["title"]): TitleTemplate =>
  typeof title === "string" ? { template: "%s", absolute: title } : title ?? ROOT_TITLE;

export const CUSTOMER_TITLE = frameOf(CUSTOMER_METADATA.title);

export const titleWith = (frame: TitleTemplate, title?: string | null): string =>
  title ? (frame.template ?? "%s").replace("%s", title) : frame.absolute;

export const NOINDEX_ROBOTS: Robots = { index: false, follow: false };

export const robotsMeta = (robots?: Robots | null): { name: string; content: string }[] =>
  robots ? [{ name: "robots", content: `${robots.index ? "index" : "noindex"}, ${robots.follow ? "follow" : "nofollow"}` }] : [];

// next 는 루트 파일 이미지(opengraph·twitter)가 있으면 화면의 제목·설명을 og·twitter 로 물려준다(resolve-metadata postProcessMetadata).
export const brandMeta = (title: string, description?: string | null) => {
  const text = description ?? ROOT_DESCRIPTION;
  return [
    { title },
    { name: "description", content: text },
    { property: "og:title", content: title },
    { property: "og:description", content: text },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: text },
  ];
};

export const brandHead = (title?: string | null) => ({ meta: brandMeta(titleWith(ROOT_TITLE, title)) });

export const customerHead = (title?: string | null) => ({ meta: [{ title: titleWith(CUSTOMER_TITLE, title) }] });

// 제목 head 뒤에 metadata.robots 를 한 줄로 붙인다.
export const withRobots = <M extends object>(head: { meta: M[] }, robots?: Robots | null) => ({ meta: [...head.meta, ...robotsMeta(robots)] });

// Worker 의 고객 화면 분기와 같은 규칙 — /s/·/c/ 아래는 legacy CUSTOMER_METADATA 가 브랜드 메타를 끊는다.
export const isCustomerPath = (pathname: string): boolean => pathname.startsWith("/s/") || pathname.startsWith("/c/");

// 루트 head — 고객 화면이면 브랜드 설명·og 없이 제목 규칙만, 아니면 legacy 루트 metadata 기본값.
export const rootHead = (matches: readonly { pathname: string }[]) =>
  matches.some((match) => isCustomerPath(match.pathname)) ? customerHead() : brandHead();

// index.html 의 화면별 기본값(data-shell) — route head 가 화면 것을 싣기 전에 걷어 같은 이름이 두 줄 남지 않게 한다.
export const SHELL_DEFAULT_SELECTOR = "[data-shell]";

export const dropShellDefaults = (head: { querySelectorAll: (selector: string) => ArrayLike<{ remove: () => void }> }) =>
  Array.from(head.querySelectorAll(SHELL_DEFAULT_SELECTOR)).forEach((node) => node.remove());

// 고객 화면이 걷는 루트 브랜딩(설명·앱 이름·아이콘·매니페스트·og·twitter) — legacy CUSTOMER_METADATA 가 끊던 목록 · Worker 셸과 SPA 가 같이 쓴다.
const BRAND_META_NAMES = new Set(["description", "application-name"]);
const BRAND_LINK_RELS = new Set(["icon", "apple-touch-icon", "manifest", "shortcut icon", "mask-icon"]);

export interface HeadTagAttrs {
  tag: string;
  name?: string | null;
  property?: string | null;
  rel?: string | null;
}

export const isBrandHeadTag = ({ tag, name, property, rel }: HeadTagAttrs): boolean => {
  if (tag.toLowerCase() === "meta") {
    const metaName = (name ?? "").toLowerCase();
    return BRAND_META_NAMES.has(metaName) || metaName.startsWith("twitter:") || (property ?? "").toLowerCase().startsWith("og:");
  }
  return tag.toLowerCase() === "link" && BRAND_LINK_RELS.has((rel ?? "").toLowerCase());
};

// 화면 head(brandMeta)가 싣는 이름 — React 가 넣고 빼므로 index.html 몫 걷기에서 뺀다.
const ROUTE_HEAD_KEYS = new Set(brandMeta("").flatMap((tag) => ("name" in tag ? [tag.name] : "property" in tag ? [tag.property] : [])));

export interface HeadNode {
  tagName: string;
  isConnected: boolean;
  getAttribute: (name: string) => string | null;
  remove: () => void;
}

// index.html 의 브랜딩 태그를 잡아 두고, 고객 화면(/s·/c)이면 걷고 아니면 되돌리는 함수를 돌려준다 — dev SPA·클라이언트 이동도 Worker 셸과 같은 head.
export const shellBrandToggle = <N extends HeadNode>(head: { querySelectorAll: (selector: string) => ArrayLike<N>; append: (node: N) => void }) => {
  const nodes = Array.from(head.querySelectorAll("meta,link")).filter((node) => {
    const attrs = { tag: node.tagName, name: node.getAttribute("name"), property: node.getAttribute("property"), rel: node.getAttribute("rel") };
    return isBrandHeadTag(attrs) && !ROUTE_HEAD_KEYS.has((attrs.name ?? attrs.property ?? "").toLowerCase());
  });
  return (pathname: string): void => {
    const customer = isCustomerPath(pathname);
    for (const node of nodes) {
      if (customer) node.remove();
      else if (!node.isConnected) head.append(node);
    }
  };
};

// main.tsx 가 그리기 전에 부른다 — data-shell 기본값을 걷고 이 주소의 브랜딩을 맞춘 뒤, 화면 이동 때 부를 맞춤 함수를 돌려준다.
export const prepareShellHead = <N extends HeadNode>(head: { querySelectorAll: (selector: string) => ArrayLike<N>; append: (node: N) => void }, pathname: string) => {
  dropShellDefaults(head);
  const sync = shellBrandToggle(head);
  sync(pathname);
  return sync;
};
